import Vue from "vue";

/**
 * Versionamento do sistema (Sales Teams — painel do consultor).
 *
 * FONTE ÚNICA: a primeira entrada de CHANGELOG é a versão atual. O selo do menu
 * lateral, a página /novidades e o sino de notificações leem daqui. Mantenha
 * `version` do package.json igual à primeira entrada.
 *
 * Como publicar uma versão nova:
 *   1. Acrescente um objeto NO TOPO do array (versão, data ISO, título, resumo
 *      e itens). Escreva para o usuário final — o que mudou para ele, não o
 *      que mudou no código.
 *   2. Suba o `version` do package.json.
 *   O selo "Nova" e o sino acendem sozinhos para quem ainda não abriu a página.
 *
 * Semântica (semver adaptado):
 *   MAIOR  (3.0.0) — mudança de experiência no sistema inteiro;
 *   MENOR  (2.1.0) — tela ou recurso novo;
 *   CORREÇÃO (2.0.1) — ajuste/correção sem recurso novo.
 *
 * Tipos de item: "novo" | "melhoria" | "correcao".
 */
export const CHANGELOG = [
  {
    version: "2.0.0",
    date: "2026-10-30",
    title: "Nova identidade visual",
    summary:
      "O Sales Teams ganhou um visual novo, mais leve e moderno, uma barra superior com busca e modo escuro.",
    items: [
      { type: "novo", text: "Busca no sistema (Ctrl K): encontre qualquer tela que você tem acesso digitando o nome." },
      { type: "novo", text: "Modo escuro: alterne entre claro e escuro pelo botão na barra superior." },
      { type: "novo", text: "Notificações: o sino avisa quando sai uma versão nova do sistema." },
      { type: "novo", text: "Página de Novidades com o histórico de atualizações." },
      { type: "novo", text: "Menu lateral recolhível, para ganhar espaço em telas menores." },
      { type: "melhoria", text: "Botão “Como funciona” no topo para rever o tutorial a qualquer momento." },
      { type: "melhoria", text: "Botões, cards, tabelas, formulários e janelas com um padrão visual único em todas as telas." },
      { type: "melhoria", text: "No celular, os filtros das listagens ficam em duas colunas e a lista aparece logo na primeira tela." },
      { type: "melhoria", text: "Etiquetas de status mais legíveis e foco de teclado visível em todos os controles." },
    ],
  },
  {
    version: "1.9.0",
    date: "2026-10-29",
    title: "Pesquisa NPS e Notas Fiscais",
    summary: "Pesquisa de satisfação pública e mais clareza no saldo das notas fiscais.",
    items: [
      { type: "novo", text: "Pesquisa de satisfação (NPS) em página pública." },
      { type: "melhoria", text: "Notas fiscais: o card mostra para onde foi o saldo unificado." },
      { type: "melhoria", text: "Notas fiscais: card conciliado pela Regional e solicitação resolvida." },
    ],
  },
  {
    version: "1.8.0",
    date: "2026-10-23",
    title: "Ranking Nacional",
    summary: "Ajustes nos períodos exibidos do Ranking Nacional.",
    items: [
      { type: "melhoria", text: "Ranking Nacional passa a exibir os períodos a partir de 09/2026." },
    ],
  },
  {
    version: "1.7.0",
    date: "2026-09-24",
    title: "Menu Rankings",
    summary: "Rankings reunidos em um só lugar do menu.",
    items: [
      { type: "novo", text: "Menu Rankings com Ranking Nacional, Regras e Regulamentos." },
    ],
  },
];

export const APP_VERSION = CHANGELOG[0].version;

/** "2.0.0" → "v2.0"; "2.0.1" → "v2.0.1" (o .0 final só some na correção zero). */
export function rotuloVersao(version) {
  const v = version || APP_VERSION;
  return "v" + v.replace(/\.0$/, "");
}

export function formatarDataVersao(iso) {
  const [ano, mes, dia] = String(iso).split("-");
  return `${dia}/${mes}/${ano}`;
}

// ---------------------------------------------------------------------------
// "Já vi esta versão?" — por aparelho (localStorage).
// ---------------------------------------------------------------------------
const CHAVE_VISTA = "st-versao-vista";

function lerVista() {
  try {
    return localStorage.getItem(CHAVE_VISTA);
  } catch (e) {
    return null;
  }
}

function comparar(a, b) {
  const pa = String(a || "0").split(".").map(Number);
  const pb = String(b || "0").split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0);
  }
  return 0;
}

export const estadoVersao = Vue.observable({ vista: lerVista() });

/** Versões publicadas depois da última que o usuário abriu em /novidades. */
export function versoesNaoVistas() {
  if (!estadoVersao.vista) return CHANGELOG.slice(0, 1);
  return CHANGELOG.filter((v) => comparar(v.version, estadoVersao.vista) > 0);
}

export function temVersaoNova() {
  return versoesNaoVistas().length > 0;
}

export function marcarVersaoVista() {
  estadoVersao.vista = APP_VERSION;
  try {
    localStorage.setItem(CHAVE_VISTA, APP_VERSION);
  } catch (e) {
    /* armazenamento bloqueado: o selo some só nesta aba */
  }
}
